import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { buildClassName } from '@utils';

import './Button.css';

export type ButtonVariant = 'primary' | 'secondary' | 'accent' | 'outline' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Button variant style
   * @default 'primary'
   */
  variant?: ButtonVariant;
  /**
   * Button size
   * @default 'md'
   */
  size?: ButtonSize;
  /**
   * Whether button is in loading state
   * @default false
   */
  loading?: boolean;
  /**
   * Whether button takes full width
   * @default false
   */
  fullWidth?: boolean;
  /**
   * Button content
   */
  children: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  className = '',
  disabled,
  children,
  ...props
}: ButtonProps) {
  const buttonClasses = buildClassName(
    'bhg-button',
    `bhg-button--${variant}`,
    `bhg-button--${size}`,
    loading && 'bhg-button--loading',
    fullWidth && 'bhg-button--full-width',
    className
  );

  return (
    <button
      className={buttonClasses}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <span className="bhg-button__spinner" aria-hidden="true" />}
      <span className="bhg-button__content">{children}</span>
    </button>
  );
}
